"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@eg/ui";
import { useAuthStore } from "@/store/auth";
import Link from "next/link";
import { ApiError } from "@/lib/api";
import { useTranslations } from "@/lib/i18n/I18nProvider";

function LoginForm() {
  const params = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const t = useTranslations();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const callbackUrl = (params.get("callbackUrl") as string) || "/";
    try {
      await login(email, password);
      window.location.href = callbackUrl;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t("auth.loginFailed"));
      } else {
        setError(t("auth.genericError"));
      }
    }
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.customerLoginTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="block mb-1">{t("auth.email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="block mb-1">{t("auth.password")}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <div className="mt-2 text-right">
                <Link
                  href="/passwort-vergessen"
                  className="text-xs text-brand-700 underline"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
            </div>
            {error && <p className="text-error text-sm">{error}</p>}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isLoading}>
                {isLoading ? t("auth.signingIn") : t("auth.signIn")}
              </Button>
              <Link
                href="/register"
                className="flex-1 text-center rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 transition hover:border-brand-300 hover:text-brand-700"
              >
                {t("auth.signUp")}
              </Link>
            </div>
          </form>
          <p className="text-xs text-gray-500">{t("auth.acceptTerms")}</p>
        </CardContent>
      </Card>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
